/**
 * Every moment the web app records, and nothing else.
 *
 * The names match the phone app's (`app/lib/core/analytics/analytics_event.dart`)
 * so one PostHog funnel covers both. Call sites name a moment and its few
 * enum-like details; they cannot pass free text, a date or an id, because
 * the type has nowhere to put one. `sanitise` below checks again anyway.
 */
export type ConversationMode = "spoken" | "typed";
export type ForecastPeriodName = "day" | "week" | "month";
export type PaywallSurface = "locked" | "upgrade";
export type Currency = "USD" | "INR";
export type SignInMethod = "google" | "email";
export type SignInStep = "start" | "send_code" | "verify_code" | "callback";
export type Failure = "no_time_left" | "not_found" | "error";

/** First run, in the order a new visitor meets it. */
type OnboardingMoment =
  | { readonly name: "get started tapped" }
  | { readonly name: "sign in tapped" }
  | { readonly name: "onboarding started" }
  | { readonly name: "birth date entered" }
  | { readonly name: "birth time entered"; readonly accuracy: "exact" | "approximate" | "unknown" }
  | { readonly name: "place searched" }
  | { readonly name: "place search failed"; readonly reason: "no_results" | "error" }
  | { readonly name: "place chosen" }
  | { readonly name: "week preview failed" }
  | { readonly name: "birth details completed" }
  | { readonly name: "chart revealed" }
  | { readonly name: "reveal continued" }
  | { readonly name: "consent given" }
  | { readonly name: "sign-in chosen"; readonly method: SignInMethod }
  | { readonly name: "email code sent" }
  | { readonly name: "email code resent" }
  | { readonly name: "sign-in failed"; readonly method: SignInMethod; readonly step: SignInStep }
  | { readonly name: "signed in"; readonly method: SignInMethod }
  | { readonly name: "talk invitation answered"; readonly choice: "talk" | "later" }
  | { readonly name: "chart save failed" }
  | { readonly name: "signed up" };

/** Today, Life and Friends. */
type ReadingMoment =
  | { readonly name: "forecast viewed"; readonly period: ForecastPeriodName }
  | { readonly name: "forecast failed"; readonly period: ForecastPeriodName }
  | { readonly name: "locked period tapped"; readonly period: ForecastPeriodName }
  | { readonly name: "forecast detail opened"; readonly detail: "moon" | "days" | "why" }
  | { readonly name: "forecast shared" }
  | { readonly name: "reading opened"; readonly reading: string }
  | { readonly name: "reading viewed"; readonly reading: string }
  | { readonly name: "reading failed"; readonly reading: string; readonly reason: Failure }
  | { readonly name: "reading work opened"; readonly reading: string }
  | { readonly name: "friend opened" }
  | { readonly name: "friend action"; readonly action: "accept" | "remove" | "block" | "report" };

/** Talking to Antar, spoken or typed. */
type ConversationMoment =
  | { readonly name: "speak tapped"; readonly from: "talk" | "today" | "welcome" }
  | { readonly name: "type tapped" }
  | { readonly name: "voice limit shown" }
  | { readonly name: "conversation started"; readonly mode: ConversationMode }
  | { readonly name: "question category chosen"; readonly category: string }
  | { readonly name: "answer consumed"; readonly mode: ConversationMode }
  | { readonly name: "ask failed"; readonly reason: Failure }
  | { readonly name: "audio unlock tapped" }
  | { readonly name: "call ended"; readonly seconds: number; readonly ended_by: "person" | "call" }
  | { readonly name: "call failed"; readonly reason: "no_time_left" | "error" }
  | { readonly name: "keep talking tapped" };

/** The plan, the paywall and the account. */
type PlanMoment =
  | { readonly name: "paywall shown"; readonly surface: PaywallSurface }
  | { readonly name: "paywall dismissed" }
  | { readonly name: "subscribe tapped"; readonly currency: Currency; readonly value: number }
  | { readonly name: "checkout failed" }
  | { readonly name: "checkout cancelled" }
  | {
      readonly name: "purchase completed";
      readonly currency: Currency;
      readonly value: number;
      /** Google's de-duplication key for a sale. Sent to Google only. */
      readonly transactionId: string;
    }
  | { readonly name: "billing portal opened" }
  | { readonly name: "language changed"; readonly language: string }
  | { readonly name: "signed out" }
  | { readonly name: "account deleted" };

export type Moment = OnboardingMoment | ReadingMoment | ConversationMoment | PlanMoment;

export type MomentName = Moment["name"];
type Params = Readonly<Record<string, string | number>>;

/** Only the three steps Google Ads bids on go to Google, under Google's own names. */
export function googleEvent(moment: Moment): readonly [string, Params] | null {
  switch (moment.name) {
    case "signed up":
      return ["sign_up", {}];
    case "subscribe tapped":
      return ["begin_checkout", { currency: moment.currency, value: moment.value }];
    case "purchase completed":
      return ["purchase", { currency: moment.currency, value: moment.value, transaction_id: moment.transactionId }];
    default:
      return null;
  }
}

/** Every moment goes to PostHog, with its details under the phone app's keys. */
export function posthogEvent(moment: Moment): readonly [MomentName, Params] {
  const { name, ...details } = moment;
  const { transactionId: _googleOnly, ...properties } = details as Record<string, string | number>;
  // The web has one plan; `term` is the key the phone's purchase events use.
  const withTerm = name === "subscribe tapped" || name === "purchase completed" ? { ...properties, term: "monthly" } : properties;
  return [name, sanitise(withTerm)];
}

/**
 * A second guard behind the types, as the phone app has: a key that sounds
 * personal, or a string long enough to be prose, is dropped rather than sent.
 */
const DENIED_KEY_FRAGMENTS = [
  "birth", "dob", "date", "time", "place", "city", "lat", "lon", "geo", "zone", "phone", "contact", "email", "name",
  "handle", "user", "question", "answer_text", "message", "transcript", "reply", "text", "note", "summary", "query",
] as const;
const MAX_STRING_LENGTH = 32;

export function sanitise(properties: Readonly<Record<string, string | number>>): Params {
  const kept: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(properties)) {
    const lower = key.toLowerCase();
    if (DENIED_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment))) continue;
    if (typeof value === "string" && (value.length > MAX_STRING_LENGTH || !Number.isNaN(Date.parse(value)))) continue;
    if (typeof value === "number" && !Number.isFinite(value)) continue;
    kept[key] = value;
  }
  return kept;
}
